import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import { pagesText } from 'src/constants/pagesText';
import { useCreateBasePolicy } from 'src/hooks/environmentWdacPolicy';
import ManagedBasePolicyForm, { DETAILS_STEP, RULE_OPTIONS_STEP } from './ManagedBasePolicyForm';

const STEP_COPY = {
  [DETAILS_STEP]: {
    label: 'Step 1 of 2 · Policy details',
    intro:
      'Author a new base policy for this environment: its details, the Microsoft template it starts from and the AppControl.ai standard rules on top.',
  },
  [RULE_OPTIONS_STEP]: {
    label: 'Step 2 of 2 · Rule options',
    intro: "Choose the policy's rule options. The defaults match our reference base policy.",
  },
};

// Creating a managed base policy, from the managed policies list or from a form generating against
// bases. Either way the created policy is handed back through onDone; a form uses it to return with
// the policy selected.
const CreateManagedBasePolicyModal = ({ open, onDone, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(DETAILS_STEP);
  const { mutateAsync: createBasePolicy, isLoading: creating } = useCreateBasePolicy();

  useEffect(() => {
    if (open) {
      setStep(DETAILS_STEP);
    }
  }, [open]);

  const handleCreate = async payload => {
    try {
      const created = await createBasePolicy(payload);
      enqueueSnackbar(`${payload.policyName} created as a managed base policy.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onDone(created);
    } catch (error) {
      // The api layer rejects with the backend's message as a plain string, and the rules behind
      // creating a policy are worth showing verbatim.
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Iconify icon="mdi:shield-plus-outline" width={22} height={22} color="#87C8E0" />
          Create Managed Base Policy
        </Stack>
        <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}>
          {STEP_COPY[step].label}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {STEP_COPY[step].intro}
          </Typography>
          <ManagedBasePolicyForm
            step={step}
            onStepChange={setStep}
            loading={creating}
            onCancel={onClose}
            onSubmit={handleCreate}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

CreateManagedBasePolicyModal.propTypes = {
  open: PropTypes.bool,
  onDone: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateManagedBasePolicyModal;
